import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import {
  clearPendingGemSlug,
  CONVERSATIONS_QUERY_KEY,
  detachConversation,
  getPendingGemSlug,
  ShareDialog,
  useChatConversation,
} from '@/features/chat';
import { gemName, useGems } from '@/features/gems';
import { getSaveHistoryPref } from '@/features/settings';
import { config } from '@/global-config';
import { paths } from '@/routes/paths';
import { getAccessToken } from '@/shared/lib/axios';

import type { ChatOutletContext } from './chat-layout-context';

import { ChatSidebar, GENERAL_GEM_ACCENT, GENERAL_GEM_ID } from './chat-sidebar';
import { ChatTopbar } from './chat-topbar';
import { GemSwitchDialog } from './gem-switch-dialog';

// localStorage key for the desktop mini-collapsed preference. Cross-tab sync
// isn't worth a storage event listener — toggling on one tab and switching to
// another is rare enough to tolerate the next page-load resync.
const SIDEBAR_MINI_KEY = 'sahib:sidebar-mini';

// Authenticated chat shell. Owns:
//   - sidebar open/closed state
//   - active gem (shared with the composer in the page)
//   - the conversation hook keyed off the URL `:conversationId`
// Passes that state down to the page via Outlet context so the page only
// renders the messages region + composer.
export function ChatLayout() {
  const { t, i18n } = useTranslation('chat');
  const { conversationId: routeConversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  // Below `md` the sidebar can't sit alongside the chat (280px would crush a
  // phone-width composer), so on mobile it's a closed overlay drawer that opens
  // on a topbar MenuButton tap. Above `md` it's always visible — toggling
  // between full width (280px) and a mini icon-rail (~72px). Matches the v1
  // frontend's NavMobile + NavVertical mechanism (different containers, one
  // visible per breakpoint), just consolidated into a single component.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  // Mobile drawer open/closed state. Default false (sidebar hidden on phones).
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Desktop mini-collapsed state. Persisted to localStorage so the user's
  // preference survives reloads — same idea as v1's `settings.state.navLayout`
  // ('vertical' | 'mini'), just stored directly because we don't have a full
  // settings store yet. Default false (start vertical).
  const [sidebarMini, setSidebarMini] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_MINI_KEY) === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SIDEBAR_MINI_KEY, sidebarMini ? '1' : '0');
  }, [sidebarMini]);
  const [activeGemId, setActiveGemId] = useState<string>(GENERAL_GEM_ID);
  // The Gem the user picked while the current conversation already has
  // messages — held pending the confirm dialog. Null ⇒ dialog closed.
  const [pendingGemId, setPendingGemId] = useState<string | null>(null);
  // Share-conversation dialog — owned here, same pattern as GemSwitchDialog.
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Gems are sourced from the API; `activeGemId` holds the active gem's slug
  // (or GENERAL_GEM_ID for the unscoped "Sahib" gem).
  const { data: gems = [] } = useGems();
  const activeGem = useMemo(() => gems.find((g) => g.slug === activeGemId), [gems, activeGemId]);
  const activeGemName = activeGem ? gemName(activeGem, i18n.language) : t('sidebar.generalGem');
  const activeGemAccent = activeGem?.accent ?? GENERAL_GEM_ACCENT;

  // Ids of conversations created *this session while "Save chat history" is off*.
  // On leaving one of these we orphan it from the user (detachConversation) so it
  // drops out of their history but stays in the DB for analytics/consumption.
  // The set is empty at mount, which keeps the leave-effect below safe under
  // StrictMode's mount/unmount probe (it only acts on ids added after creation).
  const ephemeralIdsRef = useRef<Set<string>>(new Set());

  const {
    conversation,
    messages,
    isLoading,
    isStreaming,
    error,
    sendMessage,
    setFeedback,
    regenerate,
    editMessage,
    navigateBranch,
    abort,
    stop,
  } = useChatConversation({
    conversationId: routeConversationId,
    onConversationCreated: (id) => {
      // With "Save chat history" off, mark this new conversation ephemeral so we
      // detach it from the user when they leave.
      if (!getSaveHistoryPref()) ephemeralIdsRef.current.add(id);
      // Sidebar list should pick up the new entry.
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
      // Same route element renders both `/c` and `/c/:id`, so this is a URL-
      // only swap — no remount, no refetch (the hook's `ownIdRef` dedups).
      navigate(paths.chat.conversation(id), { replace: true });
    },
    onConversationTitled: () => {
      // AI replaced the placeholder title after the 2nd message — refresh the
      // sidebar row. The topbar updates from the hook's local state.
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
    },
  });

  // Orphan an ephemeral conversation the user is leaving — but only if "Save
  // chat history" is still off (re-enabling mid-session keeps it). Best-effort;
  // the conversation stays in the DB either way, so a failed detach just leaves
  // it visible until the next attempt.
  const detachIfEphemeral = useCallback(
    (id: string) => {
      if (!ephemeralIdsRef.current.has(id) || getSaveHistoryPref()) return;
      ephemeralIdsRef.current.delete(id);
      void detachConversation(id)
        .then(() => queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY }))
        .catch(() => {
          /* leave it in the list — user can delete manually if needed */
        });
    },
    [queryClient],
  );

  // Fire the detach when the user leaves a conversation: the cleanup runs both on
  // a route-id change (new chat / switching threads) and on layout unmount
  // (navigating out of /c). The ephemeral-set guard makes it a no-op for
  // pre-existing conversations and under StrictMode's mount probe.
  useEffect(() => {
    if (!routeConversationId) return undefined;
    const id = routeConversationId;
    return () => detachIfEphemeral(id);
  }, [routeConversationId, detachIfEphemeral]);

  // Tab close / refresh: `pagehide` won't await a normal request, so use a
  // keepalive fetch (which, unlike sendBeacon, can send the Bearer header the
  // API requires) to detach the current ephemeral conversation.
  useEffect(() => {
    const handlePageHide = () => {
      const id = routeConversationId;
      if (!id || !ephemeralIdsRef.current.has(id) || getSaveHistoryPref()) return;
      const token = getAccessToken();
      void fetch(`${config.apiBaseUrl}/api/chat/conversations/${id}/detach`, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).catch(() => {});
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [routeConversationId]);

  // Keep the active Gem in sync with whichever conversation is loaded — keyed
  // on `id` only so a user's own mid-thread pick isn't immediately reverted.
  useEffect(() => {
    if (conversation) setActiveGemId(conversation.gemSlug ?? GENERAL_GEM_ID);
  }, [conversation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending-gem handoff from the /gems detail page. When the visitor opened a
  // Gem *without* a starter prompt, no message auto-sends — so we pre-select the
  // Gem here once the catalogue is loaded, so the composer + sidebar show it as
  // active before they type. The prompt case is handled in ChatPage's auto-send
  // (the created conversation carries the Gem); skip it here when a conversation
  // is already in play. Runs once.
  const pendingGemConsumed = useRef(false);
  useEffect(() => {
    if (pendingGemConsumed.current || gems.length === 0) return;
    const slug = getPendingGemSlug();
    if (!slug) {
      pendingGemConsumed.current = true;
      return;
    }
    // Only apply on a fresh /c with a real Gem; otherwise discard so it can't
    // leak into a later conversation.
    if (!routeConversationId && gems.some((g) => g.slug === slug)) {
      setActiveGemId(slug);
    }
    clearPendingGemSlug();
    pendingGemConsumed.current = true;
  }, [gems, routeConversationId]);

  // Close the mobile drawer when crossing from mobile to desktop — otherwise
  // it would stay rendered (off-screen) and could re-show if the viewport
  // shrinks back. No-op on desktop; cheap to run unconditionally.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // A Gem is bound to a conversation at creation, so switching Gems mid-thread
  // can't change this conversation's system prompt — it must open a new one.
  // Empty chat ⇒ switch silently; chat with messages ⇒ confirm first.
  const requestGemChange = useCallback(
    (nextGemId: string) => {
      if (nextGemId === activeGemId) return;
      if (messages.length === 0) {
        setActiveGemId(nextGemId);
      } else {
        setPendingGemId(nextGemId);
      }
    },
    [activeGemId, messages.length],
  );

  const confirmGemChange = useCallback(() => {
    if (!pendingGemId) return;
    abort(); // stop any in-flight stream before leaving the conversation
    setActiveGemId(pendingGemId);
    setPendingGemId(null);
    if (routeConversationId) navigate(paths.chat.root);
  }, [pendingGemId, abort, routeConversationId, navigate]);

  const handleNewConversation = () => {
    if (routeConversationId) navigate(paths.chat.root);
    if (isMobile) setDrawerOpen(false);
  };

  const handlePickConversation = (id: string) => {
    if (isMobile) setDrawerOpen(false);
    if (id === routeConversationId) return;
    navigate(paths.chat.conversation(id));
  };

  // Single toggle entry-point — both the in-sidebar control and the topbar
  // MenuButton call this. On mobile it opens/closes the drawer; on desktop it
  // flips between vertical and mini. Mirrors v1's pattern where the same
  // hamburger button drives different state depending on the breakpoint.
  const handleSidebarToggle = () => {
    if (isMobile) setDrawerOpen((v) => !v);
    else setSidebarMini((v) => !v);
  };

  const outletContext: ChatOutletContext = {
    conversation,
    messages,
    isStreaming,
    isLoading,
    error,
    sendMessage,
    setFeedback,
    regenerate,
    editMessage,
    navigateBranch,
    stop,
    activeGemId,
    setActiveGemId: requestGemChange,
  };

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        overflow: 'hidden',
        backgroundColor: 'background.default',
      }}
    >
      <ChatSidebar
        open={drawerOpen}
        variant={isMobile ? 'temporary' : 'persistent'}
        mini={sidebarMini && !isMobile}
        onToggle={handleSidebarToggle}
        onClose={() => setDrawerOpen(false)}
        activeGemId={activeGemId}
        onPickGem={requestGemChange}
        activeConversationId={routeConversationId}
        onPickConversation={handlePickConversation}
        onNewConversation={handleNewConversation}
      />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <ChatTopbar
          title={conversation?.title ?? t('empty.title')}
          gemBadge={activeGemName}
          gemAccent={activeGemAccent}
          onSidebarToggle={handleSidebarToggle}
          onShareClick={() => setShareDialogOpen(true)}
          canShare={Boolean(conversation)}
        />
        <Outlet context={outletContext} />
      </Box>

      <GemSwitchDialog
        open={pendingGemId !== null}
        onConfirm={confirmGemChange}
        onCancel={() => setPendingGemId(null)}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        conversationId={conversation?.id ?? null}
        conversationTitle={conversation?.title ?? t('empty.title')}
        messages={messages}
      />
    </Box>
  );
}
